// RootApp.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaBook, FaDollarSign, FaQuestionCircle, FaPlus } from 'react-icons/fa';
import App from './components/App';
import LandingPage from './components/LandingPage';
import PaymentPage from './components/PaymentPage';
import SuccessPage from './components/SuccessPage';
import CancelPage from './components/CancelPage';
import ErrorBoundary from './components/ErrorBoundary';
import PrivacyPolicy from './components/PrivacyPolicy';
import Header from './components/Header';
import ProfilePage from './components/ProfilePage';
import SignInScreen from './components/SignInScreen';
import QuizList from './components/QuizList';
import QuizPage from './components/QuizPage';
import ResultsPage from './components/ResultsPage';
import FlashcardList from './components/FlashcardList';
import FlashcardPage from './components/FlashcardPage';
import PodcastList from './components/PodcastList';
import PodcastPage from './components/PodcastPage';
import VideoList from './components/VideoList';
import VideoPage from './components/VideoPage';
import HelpModal from './components/HelpModal';
import Upload from './components/Upload';
import './styles/styles.css';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { ThemeProvider } from './utils/ThemeContext';

const AppLayout = () => {
    const { authState, setAuthData } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const userName = authState?.userName || 'Guest';
    const isSignedIn = !!authState?.userId;
    const [isSidebarExpanded, setIsSidebarExpanded] = React.useState(false);
    const [showHelp, setShowHelp] = React.useState(false);
    const signInScreenRef = React.useRef(null);
    const [signInLoading, setSignInLoading] = React.useState(false);
    const [isSignInReady, setIsSignInReady] = React.useState(false);
    const [newDocument, setNewDocument] = React.useState(null);

    const handleHeaderSignInClick = React.useCallback(() => {
        if (signInScreenRef.current) {
            signInScreenRef.current.triggerSignIn();
        }
    }, []);

    const handleSignIn = React.useCallback((userData) => {
        setAuthData(
            userData.user_id,
            `${userData.firstname} ${userData.lastname}`,
            userData.accessToken,
            userData.refreshToken,
            userData.email,
            () => {
                console.log('User signed in');
            }
        );
    }, [setAuthData]);

    const toggleSidebar = () => {
        setIsSidebarExpanded((prev) => !prev);
    };

    const handleDocumentUpdate = React.useCallback((doc) => {
        setNewDocument(doc);
    }, []);

    const isPrivacyPage = location.pathname === '/privacy-policy';
    const mainClassName = React.useMemo(() => {
        let baseClass = 'main-content';
        if (isPrivacyPage) {
            baseClass += ' privacy-main-content';
        }
        baseClass += isSignedIn ? ' signed-in' : ' signed-out';
        return baseClass;
    }, [isPrivacyPage, isSignedIn]);

    const menuItems = [
        { icon: FaHome, label: 'Home', path: '/', page: 'Home' },
        { icon: FaBook, label: 'Library', path: '/documents', page: 'Library' },
        { icon: FaPlus, label: 'Upload', path: '/upload', page: 'Upload', itemClass: 'sidebar-item--accent' },
        { icon: FaDollarSign, label: 'Wallet', path: '/upgrade', page: 'Wallet' },
        { icon: FaQuestionCircle, label: 'Help', onClick: () => setShowHelp(true) },
    ];

    const renderSidebar = () => (
        <div className={`sidebar ${isSidebarExpanded ? 'expanded' : ''}`}>
            <nav className="sidebar-menu">
                <ul>
                    {menuItems.map((item, index) => (
                        <li
                            className={`sidebar-menu-item ${item.itemClass || ''}`}
                            key={index}
                            onClick={() => {
                                if (item.onClick) {
                                    item.onClick();
                                } else {
                                    navigate(item.path);
                                    if (window.innerWidth <= 768) {
                                        setIsSidebarExpanded(false);
                                    }
                                }
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <item.icon className="sidebar-icon" />
                            {isSidebarExpanded && <span className="menu-label">{item.label}</span>}
                        </li>
                    ))}
                </ul>
            </nav>

            {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
        </div>
    );

    const renderSignInScreen = () => (
        <SignInScreen
            ref={signInScreenRef}
            onSignIn={handleSignIn}
            onLoadingChange={setSignInLoading}
            onReadyChange={setIsSignInReady}
            showInlineButton={false}
        />
    );

    return (
        <div className="app-container">
            <Header
                userName={userName}
                isSignedIn={isSignedIn}
                onSignInClick={handleHeaderSignInClick}
                isSigningIn={signInLoading}
                canTriggerSignIn={isSignInReady}
                onMenuToggle={toggleSidebar}
                isSidebarExpanded={isSidebarExpanded}
            />
            {isSignedIn && renderSidebar()}
            <main className={mainClassName}>
                <Routes>
                    <Route
                        path="/"
                        element={isSignedIn ? <LandingPage newDocument={newDocument} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/documents"
                        element={isSignedIn ? <App isSignedIn={isSignedIn} newDocument={newDocument} onDocumentUpdate={handleDocumentUpdate} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/upload"
                        element={isSignedIn ? <Upload onDocumentUpdate={handleDocumentUpdate} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/quiz-list"
                        element={isSignedIn ? <QuizList accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/quiz"
                        element={isSignedIn ? <QuizPage accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/flashcard-list"
                        element={isSignedIn ? <FlashcardList accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/flashcard"
                        element={isSignedIn ? <FlashcardPage accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/podcast-list"
                        element={isSignedIn ? <PodcastList accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/podcast"
                        element={isSignedIn ? <PodcastPage accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/video-list"
                        element={isSignedIn ? <VideoList accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/video-page"
                        element={isSignedIn ? <VideoPage accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/results"
                        element={isSignedIn ? <ResultsPage accessToken={authState?.accessToken} supabaseRefreshToken={authState?.supabaseRefreshToken || ''} onSignIn={handleSignIn} /> : renderSignInScreen()}
                    />
                    <Route
                        path="/upgrade"
                        element={isSignedIn ? <PaymentPage /> : renderSignInScreen()}
                    />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                        path="/profile"
                        element={isSignedIn ? <ProfilePage /> : renderSignInScreen()}
                    />
                    <Route path="/success" element={<SuccessPage />} />
                    <Route path="/cancel" element={<CancelPage />} />
                    <Route path="*" element={<div>Page not found</div>} />
                </Routes>
            </main>
            <footer className="app-footer">&copy; {new Date().getFullYear()} LAADU LLC</footer>
        </div>
    );
};

const RootApp = () => (
    <Router>
        <ErrorBoundary>
            <ThemeProvider>
                <AuthProvider>
                    <AppLayout />
                </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    </Router>
);

export default RootApp;